import { Check, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { LazyImage } from "@/components/ui/lazy-image";
import type { AdStyle } from "./types";

interface StyleCardProps {
  style: AdStyle;
  isSelected: boolean;
  onToggle: (id: string) => void;
  onExpand?: (style: AdStyle) => void;
  disabled?: boolean;
}

export function StyleCard({ style, isSelected, onToggle, onExpand, disabled }: StyleCardProps) {
  const canToggle = !disabled || isSelected;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => canToggle && onToggle(style.id)}
      onKeyDown={(e) => e.key === "Enter" && canToggle && onToggle(style.id)}
      className={cn(
        "break-inside-avoid relative group cursor-pointer",
        "rounded-xl overflow-hidden transition-all duration-200",
        isSelected
          ? "ring-2 ring-primary ring-offset-2"
          : "hover:scale-[1.02]",
        disabled && !isSelected && "opacity-40 cursor-not-allowed"
      )}
    >
      {/* Image */}
      <LazyImage
        src={style.coverImage}
        alt={style.name}
        className="w-full block"
        wrapperClassName="w-full"
      />

      {/* Subtle gradient for expand button visibility */}
      <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-black/20 to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

      {/* Expand button — top left, visible on hover */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onExpand?.(style);
        }}
        className="absolute top-2.5 left-2.5 w-6 h-6 bg-white/15 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-white/30 z-10"
        title="Ver preview"
      >
        <Maximize2 className="w-3 h-3 text-white/90" strokeWidth={2} />
      </button>

      {/* Selected checkmark — bottom left, always visible */}
      {isSelected && (
        <div className="absolute bottom-2 left-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center z-10 shadow-md">
          <Check className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={3} />
        </div>
      )}
    </div>
  );
}
